/**
 * A restaurant's serving hours are one document holding groups of several
 * kinds: opening (`store_hours`), menu and reservation hours. Saving replaces
 * the whole list, so each page edits only its own kind and keeps the rest.
 */
export type HourType = 'store_hours' | 'menu_hours' | 'reservation_hours';

export interface HoursRange {
  start_time: string;
  end_time: string;
}

export interface ServingHoursGroup {
  days_of_the_week: string[];
  hours: HoursRange[];
  hour_type?: HourType;
}

/**
 * A date that doesn't follow the weekly hours (FNF-1179): closed all day, or
 * open on its own ranges only. Saved one by one, not with the weekly hours.
 */
export type DateExceptionKind = 'closed' | 'hours';

export interface DateException {
  /** YYYY-MM-DD on the restaurant's clock. */
  date: string;
  kind: DateExceptionKind;
  hours: HoursRange[];
  /** Shown to guests too, through the restaurant's public info. */
  note?: string | null;
}

/**
 * Closed for now, whatever the hours say (FNF-1612). `closed_until` is a UTC
 * instant, or null until someone reopens.
 */
export interface TemporaryClosure {
  closed_until: string | null;
  reason: string | null;
  set_at: string | null;
}

export interface ServingHoursDoc {
  serving_hour_id?: string | null;
  serving_hours?: ServingHoursGroup[] | null;
  date_exceptions?: DateException[] | null;
  temporary_closure?: TemporaryClosure | null;
}

/** "Thu, Dec 25" for 2026-12-25; the date is a calendar date, so no zone. */
export function dateLabel(serviceDate: string, withYear = false): string {
  const [y, m, d] = serviceDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

/** The exception set for a date, if any. */
export function exceptionOn(
  doc: ServingHoursDoc | null | undefined,
  serviceDate: string,
): DateException | null {
  return (doc?.date_exceptions ?? []).find((e) => e.date === serviceDate) ?? null;
}

/** Days in display order, spelled as the API stores them. */
export const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Groups saved before hour types existed are opening hours. */
export function hourTypeOf(group: ServingHoursGroup): HourType {
  return group.hour_type ?? 'store_hours';
}

export function groupsOfType(
  groups: ServingHoursGroup[] | null | undefined,
  type: HourType,
): ServingHoursGroup[] {
  return (groups ?? []).filter((g) => hourTypeOf(g) === type);
}

/** `groups` with every group of `type` swapped for `replacement`. */
export function withGroupsOfType(
  groups: ServingHoursGroup[] | null | undefined,
  type: HourType,
  replacement: ServingHoursGroup[],
): ServingHoursGroup[] {
  return [
    ...(groups ?? []).filter((g) => hourTypeOf(g) !== type),
    ...replacement.map((g) => ({ ...g, hour_type: type })),
  ];
}

/** The ranges a set of groups gives one weekday, in start order. */
export function rangesForDay(groups: ServingHoursGroup[], day: string): HoursRange[] {
  return groups
    .filter((g) => (g.days_of_the_week ?? []).includes(day))
    .flatMap((g) => g.hours ?? [])
    .filter((h) => h.start_time && h.end_time)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
}

export const DAY_MINUTES = 24 * 60;

/** Minutes from the day's midnight; an end at or before the start is the next day. */
export function hoursSpan(r: HoursRange): [number, number] {
  const toMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };
  const start = toMinutes(r.start_time);
  const end = toMinutes(r.end_time);
  return [start, end <= start ? end + DAY_MINUTES : end];
}

export function dayBefore(day: string): string {
  const i = WEEK_DAYS.indexOf(day as (typeof WEEK_DAYS)[number]);
  return WEEK_DAYS[(i + WEEK_DAYS.length - 1) % WEEK_DAYS.length];
}

/**
 * The hours one weekday takes bookings, as reservation-service works them
 * out (FNF-1515): its booking hours clipped to its opening hours, including
 * the part of the day before's that runs past midnight. A day whose booking
 * hours fall wholly outside takes its opening hours instead; a day with no
 * booking hours takes none. With only one kind set, that kind is used as is.
 * A group with no days applies to every day.
 */
export function bookingRangesForDay(
  booking: ServingHoursGroup[],
  opening: ServingHoursGroup[],
  day: string,
): HoursRange[] {
  const on = (groups: ServingHoursGroup[], d: string) =>
    groups
      .filter((g) => !(g.days_of_the_week ?? []).length || g.days_of_the_week.includes(d))
      .flatMap((g) => g.hours ?? [])
      .filter((h) => h.start_time && h.end_time);
  if (!booking.length || !opening.length) {
    return on(booking.length ? booking : opening, day);
  }
  const ranges = on(booking, day).map(hoursSpan);
  if (!ranges.length) {
    return [];
  }
  const own = on(opening, day);
  const windows = [
    ...own.map(hoursSpan),
    ...on(opening, dayBefore(day))
      .map(hoursSpan)
      .map(([s, e]): [number, number] => [s - DAY_MINUTES, e - DAY_MINUTES]),
  ];
  const clock = (minutes: number) => {
    const m = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  };
  const clipped = ranges.flatMap(([start, end]) =>
    windows
      .map(([wStart, wEnd]): [number, number] => [Math.max(start, wStart), Math.min(end, wEnd)])
      .filter(([s, e]) => s < e)
      .map(([s, e]) => ({ start_time: clock(s), end_time: clock(e) })),
  );
  return clipped.length ? clipped : own;
}

/**
 * Days with the same ranges share one group, in week order, so the saved
 * document stays as compact as the one the profile page writes.
 */
export function groupByRanges(days: { day: string; ranges: HoursRange[] }[]): ServingHoursGroup[] {
  const byRanges = new Map<string, ServingHoursGroup>();
  for (const { day, ranges } of days) {
    if (!ranges.length) {
      continue;
    }
    const key = ranges.map((r) => `${r.start_time}-${r.end_time}`).join(',');
    const group = byRanges.get(key);
    if (group) {
      group.days_of_the_week.push(day);
    } else {
      byRanges.set(key, { days_of_the_week: [day], hours: ranges.map((r) => ({ ...r })) });
    }
  }
  return [...byRanges.values()];
}

/** How far ahead a closed date may be set; restaurant-service allows 730 days. */
export const MAX_EXCEPTION_DAYS_AHEAD = 730;

/** Hours a day starts with when it has no opening hours to start from. */
export const DEFAULT_BOOKING_RANGE: HoursRange = { start_time: '17:00', end_time: '21:00' };

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** "17:00" → "5:00 PM". */
export function to12h(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * Why one day's booking hours can't be saved, or null. An end at or before
 * the start runs past midnight, as opening hours may (17:00–01:00).
 */
export function bookingDayError(ranges: HoursRange[]): string | null {
  if (!ranges.length) {
    return 'Add the hours bookings are taken, or switch the day off.';
  }
  const spans: [number, number][] = [];
  for (const r of ranges) {
    if (!r.start_time || !r.end_time) {
      return 'Enter both a start and an end time.';
    }
    const start = toMinutes(r.start_time);
    let end = toMinutes(r.end_time);
    if (end === start) {
      return 'Start and end time are the same.';
    }
    if (end < start) {
      end += 24 * 60;
    }
    spans.push([start, end]);
  }
  spans.sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < spans.length; i++) {
    if (spans[i][0] < spans[i - 1][1]) {
      return 'These hours overlap.';
    }
  }
  return null;
}
